import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Modal,
  Badge,
  Image,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Form,
} from 'react-bootstrap';
//import { PencilFill, ShareFill } from 'react-bootstrap-icons';

import { ReturnedPortfolio } from '../Create-Portfolio/types';
import './Preview.css';
import { API_ROUTES } from '../../config/api-routes';
import axiosInstance from '../../api/api';
import { useNavigate } from 'react-router-dom';
import {
  ROLE_CHANGE_WARNING,
  canShareProfile,
  shareProfileBlockMessage,
  startRoleSelectionFlow,
} from '../../utils/userProfile';
import { Prompt } from '../UserProfile/PromptDataTabContent';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import { ReactComponent as PlusIcon } from '../../images/plus.svg';
import { ReactComponent as ShareIcon } from '../../images/share-fill.svg';
import SkillsInput from '../Skills-Input';
import ShareProfileModal from '../ShareProfile/ShareProfileModal';
import { buildShareCandidateInfo } from '../ShareProfile/buildShareCandidateInfo';
import {
  buildCandidateProfilePreviewUrl,
  buildRecruiterProfileUrl,
} from '../../utils/recruiterProfileUrl';
import ResumeUpload from '../ResumeUpload';
import { toast } from 'react-toastify';
import { InfoCircle } from 'react-bootstrap-icons';
import { getSessionStore, useSessionStore } from '../../store/sessionStore';
import { computeUserProgress } from '../../utils/sessionProgress';
import { normalizeSkillsList } from '../../utils/userProfile';

const MAX_PROFILE_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

interface PanelProps extends promptsTabProps {
  userData: any;
  portfolioId?: number;
  portfolio?: ReturnedPortfolio;
  savePortfolioButton?: boolean;
  setSavePortfolioButton?: React.Dispatch<React.SetStateAction<boolean>>;
  getData?: (options?: { force?: boolean }) => void | Promise<void>;
  setUserData?: React.Dispatch<any>;
  jobTitle: string;
  hideProfessionalLinks?: boolean;
}

interface promptsTabProps {
  prompts?: Prompt[];
  setPromptsData?: Dispatch<SetStateAction<any>>;
}

const UserPanel: React.FC<PanelProps> = ({
  prompts,
  userData,
  setUserData,
  jobTitle,
  setPromptsData,
  hideProfessionalLinks = false,
  getData,
}) => {
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [skills, setSkills] = useState<string[]>([]);
  // const [showModal, setShowModal] = useState<boolean>(false);
  const [show, setShareModal] = useState<boolean>(false);
  const [showEditJobRoleModal, setShowEditJobRoleModal] = useState(false);
  const [showDataWarningModal, setShowDataWarningModal] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareDurationDays, setShareDurationDays] = useState(30);
  const [skillsSaveLoading, setSkillsSaveLoading] = useState(false);

  const [profileLink, setProfileLink] = useState('');
  const [profilePreviewHref, setProfilePreviewHref] = useState('');

  const [showImageModal, setShowImageModal] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [linksSaveLoading, setLinksSaveLoading] = useState(false);
  const [professionalLinks, setProfessionalLinks] = useState<string[]>(['']);
  const [recruiterEmail, setRecruiterEmail] = useState('');
  const [shareEmailSuccess, setShareEmailSuccess] = useState(false);
  const [shareEmailError, setShareEmailError] = useState(false);

  //const handleImageClick = () => setShowImageModal(true);
  const handleCloseModal = () => setShowImageModal(false);
  const MAX_SHOWN = 5;

  const navigate = useNavigate();
  const sessionPrompts = useSessionStore((s) => s.prompts);
  const sessionPortfolio = useSessionStore((s) => s.portfolio as ReturnedPortfolio | null);
  const promptsForShare = prompts ?? sessionPrompts;
  const shareAllowed = canShareProfile(promptsForShare);
  const shareBlockMessage = shareProfileBlockMessage(promptsForShare);

  const shareCandidate = useMemo(() => {
    const progress = computeUserProgress(
      userData,
      sessionPrompts,
      sessionPortfolio as Record<string, unknown> | null
    );
    return buildShareCandidateInfo({
      user: userData,
      jobTitle,
      prompts: sessionPrompts,
      portfolio: sessionPortfolio,
      profileCompletePercent: progress.percent,
    });
  }, [userData, jobTitle, sessionPrompts, sessionPortfolio]);

  useEffect(() => {
    setSkills(normalizeSkillsList(userData?.skills));
    const username = String(userData?.username || '');
    setProfileLink(buildRecruiterProfileUrl(username));
    setProfilePreviewHref(buildCandidateProfilePreviewUrl(username));
    const normalizedLinks: string[] = [];
    if (userData?.linkedin) {
      normalizedLinks.push(String(userData.linkedin));
    }

    const rawOtherSocial = userData?.otherSocial;
    if (Array.isArray(rawOtherSocial)) {
      rawOtherSocial.forEach((entry) => {
        if (entry) normalizedLinks.push(String(entry));
      });
    } else if (typeof rawOtherSocial === 'string' && rawOtherSocial.trim()) {
      try {
        const parsed = JSON.parse(rawOtherSocial);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry) => {
            if (entry) normalizedLinks.push(String(entry));
          });
        } else {
          normalizedLinks.push(rawOtherSocial);
        }
      } catch {
        normalizedLinks.push(rawOtherSocial);
      }
    }

    const deduped = normalizedLinks.filter(
      (link, idx, arr) => link.trim() && arr.indexOf(link) === idx
    );
    setProfessionalLinks(deduped.length > 0 ? deduped.slice(0, 10) : ['']);
  }, [userData?.skills, userData?.username, userData?.linkedin, userData?.otherSocial]);

  const handleUpdateSkills = (updatedSkills: string[]) => setSkills(updatedSkills);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSkillsSaveLoading(true);

    try {
      const normalizedSkills = normalizeSkillsList(skills);
      const response = await axiosInstance.put(API_ROUTES.USER.UPDATE_USER, {
        skills: normalizedSkills,
      });

      if (!response || response.status !== 200) {
        throw new Error(`Failed to update skills: ${response.statusText}`);
      }

      const savedSkills = normalizeSkillsList(response.data?.user?.skills ?? normalizedSkills);
      setUserData?.((prevState: any) => ({
        ...prevState,
        skills: savedSkills,
      }));
      setSkills(savedSkills);
      useSessionStore.getState().updateCurrentUser({ skills: savedSkills });
      setShowModal(false);
      toast.success('Skills updated successfully!');
    } catch (error) {
      console.error('Failed to update skills:', error);
      toast.error('Failed to update skills');
    } finally {
      setSkillsSaveLoading(false);
    }
  };

  const handleSaveLinks = async () => {
    setLinksSaveLoading(true);
    try {
      const cleaned = professionalLinks
        .map((link) => link.trim())
        .filter((link, idx, arr) => Boolean(link) && arr.indexOf(link) === idx)
        .slice(0, 10);
      const [linkedin, ...otherLinks] = cleaned;

      const response = await axiosInstance.put(API_ROUTES.USER.UPDATE_USER, {
        linkedin: linkedin || '',
        otherSocial: JSON.stringify(otherLinks),
      });

      if (!response || response.status !== 200) {
        throw new Error(`Failed to update links: ${response?.statusText}`);
      }

      setUserData?.((prevState: any) => ({
        ...prevState,
        linkedin: linkedin || '',
        otherSocial: JSON.stringify(otherLinks),
      }));
      useSessionStore.getState().updateCurrentUser({
        linkedin: linkedin || '',
        otherSocial: JSON.stringify(otherLinks),
      });
      setShowLinksModal(false);
      toast.success('Professional links updated successfully!');
    } catch (error) {
      console.error('Failed to update links:', error);
      toast.error('Failed to update links');
    } finally {
      setLinksSaveLoading(false);
    }
  };

  const handleProfessionalLinkChange = (index: number, value: string) => {
    setProfessionalLinks((prev) => prev.map((link, idx) => (idx === index ? value : link)));
  };

  const addProfessionalLinkField = () => {
    setProfessionalLinks((prev) => (prev.length >= 10 ? prev : [...prev, '']));
  };

  const removeProfessionalLinkField = (index: number) => {
    setProfessionalLinks((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const visibleProfessionalLinks = professionalLinks.map((link) => link.trim()).filter(Boolean);

  const getProfessionalLinkLabel = (rawLink: string) => {
    try {
      const normalized = rawLink.startsWith('http') ? rawLink : `https://${rawLink}`;
      const parsed = new URL(normalized);
      const host = parsed.hostname.replace(/^www\./, '');
      const path = parsed.pathname.replace(/\/$/, '');
      return path ? `${host}${path}` : host;
    } catch {
      return rawLink;
    }
  };

  const handleShareWithRecruiter = async () => {
    setShareLoading(true);
    setShareEmailSuccess(false);
    setShareEmailError(false);
    try {
      const response = await axiosInstance.post(API_ROUTES.RECRUITER.SHARE_PROFILE, {
        recruiterEmail: recruiterEmail.trim(),
        durationDays: shareDurationDays,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Invitation sent successfully!');
        setShareEmailSuccess(true);
        setRecruiterEmail('');
      } else {
        throw new Error('Failed to send invitation');
      }
    } catch (err: any) {
      console.error('Error sharing with recruiter:', err);
      setShareEmailError(true);
      toast.error('Failed to send invitation');
    } finally {
      setShareLoading(false);
    }
  };

  const handleEditAvatar = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }

    if (file.size > MAX_PROFILE_IMAGE_SIZE_BYTES) {
      toast.error('Image must be 10MB or smaller.');
      return;
    }

    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('profileImg', file);
      const response = await axiosInstance.put(API_ROUTES.USER.UPDATE_USER, formData);
      if (!response || response.status !== 200) {
        throw new Error('Upload failed');
      }
      const updatedImg =
        response.data?.profileImg ||
        response.data?.user?.profileImg ||
        response.data?.data?.profileImg;
      if (updatedImg) {
        setUserData?.((prev: any) => (prev ? { ...prev, profileImg: updatedImg } : prev));
        useSessionStore.getState().updateCurrentUser({ profileImg: updatedImg });
      } else if (getData) {
        await getData({ force: true });
      }
      toast.success('Profile photo updated.');
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast.error('Failed to upload profile photo.');
    } finally {
      setAvatarUploading(false);
    }
  };
  const onModalOpen = () => setShowModal(true);
  const onShareModalOpen = () => setShareModal(true);
  const onErrorModalOpen = () => setShowErrorModal(true);

  const onModalClose = () => {
    setShowModal(false);
    setSkills(normalizeSkillsList(userData?.skills));
  };

  const handleCloseShareModal = () => {
    setShareModal(false);
    setRecruiterEmail('');
    setShareEmailSuccess(false);
    setShareEmailError(false);
  };

  const copyToClipboard = async () => {
    if (!profileLink) {
      toast.error('Profile link is not available yet.');
      return;
    }
    await navigator.clipboard.writeText(profileLink);
    toast.success('Link copied to clipboard!');
  };

  const handleEditClick = () => {
    setShowEditJobRoleModal(true);
  };

  const handleEditJobRoleConfirm = () => {
    setShowEditJobRoleModal(false);
    setShowDataWarningModal(true);
  };

  const handleDataWarningConfirm = () => {
    handleCloseModals();
    startRoleSelectionFlow(navigate);
  };

  const handleCloseModals = () => {
    setShowEditJobRoleModal(false);
    setShowDataWarningModal(false);
  };

  useEffect(() => {
    const button = document.getElementById('editImageButton');
    if (button) {
      button.classList.add('shake');
      setTimeout(() => {
        button.classList.remove('shake');
      }, 2000); // stop after 2s
    }
  }, []);

  //const fullName = `${userData?.firstName} ${userData?.lastName}`;
  const visibleSkills = showAllSkills ? skills : skills.slice(0, MAX_SHOWN);
  const remainingSkills = Math.max(0, skills.length - MAX_SHOWN);

  return (
    <>
      <Card className="profile-card mb-3">
        <div className="profile-header">
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="d-none"
            aria-hidden
            tabIndex={-1}
            data-profile-focus="avatar"
            onChange={handleAvatarFileChange}
          />
          <div className="profile-image-wrapper">
            {userData?.profileImg ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={userData.profileImg || '/avatar.png'}
                  alt="Profile Avatar"
                  className="profile-avatar"
                  onClick={() => {
                    if (userData?.profileImg) {
                      setShowImageModal(true);
                    } else {
                      handleEditAvatar();
                    }
                  }}
                  style={{ cursor: 'pointer' }} // 👈 Indicate it's clickable
                />
                <button
                  type="button"
                  title="Edit Avatar"
                  className="user-info-edit-button profile-avatar-edit-btn"
                  onClick={handleEditAvatar}
                  disabled={avatarUploading}
                >
                  {avatarUploading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <EditIcon style={{ width: '15px', height: '15px' }} />
                  )}
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <style>
                  {`
                    @keyframes shake {
                      0% { transform: translate(1px, 1px) rotate(0deg); }
                      10% { transform: translate(-1px, -2px) rotate(-1deg); }
                      20% { transform: translate(-3px, 0px) rotate(1deg); }
                      30% { transform: translate(3px, 2px) rotate(0deg); }
                      40% { transform: translate(1px, -1px) rotate(1deg); }
                      50% { transform: translate(-1px, 2px) rotate(-1deg); }
                      60% { transform: translate(-3px, 1px) rotate(0deg); }
                      70% { transform: translate(3px, 1px) rotate(-1deg); }
                      80% { transform: translate(-1px, -1px) rotate(1deg); }
                      90% { transform: translate(1px, 2px) rotate(0deg); }
                      100% { transform: translate(1px, -2px) rotate(-1deg); }
                    }

                    @keyframes colorChange {
                      0%   { background-color: #ff4d4d; color: white; }
                      25%  { background-color: #4d94ff; color: white; }
                      50%  { background-color: #4dff88; color: black; }
                      75%  { background-color: #ffd24d; color: black; }
                      100% { background-color: #ff4d4d; color: white; }
                    }

                    #editImageButton {
                      animation: shake 2.5s infinite, colorChange 3s infinite;
                      border: none;
                      padding: 8px;
                      border-radius: 50%;
                      cursor: pointer;
                    }
                  `}
                </style>
                <img
                  src={userData?.profileImg || '/avatar.png'}
                  alt="Profile Avatar"
                  className="profile-avatar profile-photo"
                  onClick={() => {
                    if (userData?.profileImg) {
                      setShowImageModal(true);
                    } else {
                      handleEditAvatar();
                    }
                  }}
                />
                <button
                  type="button"
                  id="editImageButton"
                  title="Edit Avatar"
                  className="user-info-edit-button profile-avatar-edit-btn"
                  onClick={handleEditAvatar}
                  disabled={avatarUploading}
                >
                  {avatarUploading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <EditIcon style={{ width: '15px', height: '15px' }} />
                  )}
                </button>
              </div>
            )}
          </div>

          <Button
            variant="light"
            size="sm"
            className="position-absolute top-0 end-0"
            onClick={handleEditClick}
            style={{
              fontSize: '0.75rem',
              fontWeight: 500,
              backgroundColor: '#198754',
              color: 'white',
              transform: 'translate(-20%, 50%)',
            }}
          >
            Change Role
          </Button>
        </div>

        <Card.Body className="profile-body">
          <h3 className="profile-name">
            {userData?.firstName && userData?.lastName
              ? `${userData.firstName} ${userData.lastName}`
              : 'John Doe'}
          </h3>
          {jobTitle?.trim() ? <h2 className="profile-title">{jobTitle.trim()}</h2> : null}

          {/* Edit Job Role Modal */}
          <Modal show={showEditJobRoleModal} onHide={handleCloseModals} centered>
            <Modal.Header closeButton>
              <Modal.Title>Edit Job Role</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to modify your job role?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>
                No
              </Button>
              <Button variant="success" onClick={handleEditJobRoleConfirm}>
                Yes
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Data Deletion Warning Modal */}
          <Modal show={showDataWarningModal} onHide={handleCloseModals} centered>
            <Modal.Header closeButton>
              <Modal.Title>Warning</Modal.Title>
            </Modal.Header>
            <Modal.Body>{ROLE_CHANGE_WARNING}</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>
                Cancel
              </Button>
              <Button variant="success" onClick={handleDataWarningConfirm}>
                Continue to role selection
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Modal Trigger for Skills */}
          <div
            className="skills-container"
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {visibleSkills.map((skill: any, idx: any) => (
              <Badge key={idx} bg="success" className="px-3 py-2 rounded-pill text-capitalize">
                {skill}
              </Badge>
            ))}
            {!showAllSkills && remainingSkills > 0 && (
              <Badge
                as="button"
                bg="secondary"
                className="px-3 py-2 rounded-pill border-0"
                style={{ cursor: 'pointer' }}
                title={`Show all ${skills.length} skills`}
                onClick={() => setShowAllSkills(true)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowAllSkills(true);
                  }
                }}
              >
                +{remainingSkills} more
              </Badge>
            )}

            <button
              type="button"
              title="Add Skills"
              className="btn btn-outline-success btn-sm add-skills-btn d-flex align-items-center gap-1"
              onClick={onModalOpen}
            >
              <PlusIcon style={{ width: '14px', height: '14px' }} />
              Add Skills
            </button>
          </div>
          {showAllSkills && (
            <div className="d-flex justify-content-center mt-2">
              <Badge
                bg="light"
                text="dark"
                className="px-3 py-2 rounded-pill"
                onClick={() => setShowAllSkills(false)}
                style={{ cursor: 'pointer' }}
              >
                Show less
              </Badge>
            </div>
          )}

          {!hideProfessionalLinks && (
            <div className="professional-links-block mt-3">
              <div className="professional-links-header">
                <div className="professional-links-title">Professional Links</div>
                <button
                  type="button"
                  title="Edit Professional Links"
                  className="professional-links-edit-btn"
                  onClick={() => setShowLinksModal(true)}
                >
                  <PlusIcon style={{ width: '13px', height: '13px' }} />
                  Edit
                </button>
              </div>

              {visibleProfessionalLinks.length ? (
                <div className="professional-links-list">
                  {visibleProfessionalLinks.map((link, index) => (
                    <a
                      key={`display-link-${index}`}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="professional-link-item"
                      title={link}
                    >
                      <span>{getProfessionalLinkLabel(link)}</span>
                      <span className="professional-link-arrow" aria-hidden="true">
                        ↗
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="professional-links-empty">
                  Add your LinkedIn, GitHub, portfolio, or certifications.
                </p>
              )}
              <div className="professional-links-footnote">Up to 10 links supported.</div>
            </div>
          )}

          {/* Modal for Editing Skills */}
          <Modal
            className="ss-app-modal"
            show={showModal}
            onHide={onModalClose}
            centered
            scrollable
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: '20px' }}>
                Skills
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="skills-tooltip">
                      Press <b>Enter</b> after typing a skill to add it before clicking Save.
                    </Tooltip>
                  }
                >
                  <InfoCircle style={{ cursor: 'pointer', color: '#0d6efd' }} />
                </OverlayTrigger>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <SkillsInput skills={skills} onUpdateSkills={handleUpdateSkills} />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="success"
                onClick={handleSubmit}
                disabled={skillsSaveLoading}
                style={{
                  fontWeight: '700',
                  fontSize: '16px',
                  borderRadius: '4px',
                }}
              >
                {skillsSaveLoading ? <Spinner animation="border" size="sm" /> : 'Save'}
              </Button>
            </Modal.Footer>
          </Modal>

          {!hideProfessionalLinks && (
            <Modal
              className="ss-app-modal"
              show={showLinksModal}
              onHide={() => setShowLinksModal(false)}
              centered
              scrollable
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '20px' }}>Professional Links</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-muted mb-3">
                  Add up to 10 professional links (LinkedIn, GitHub, portfolio, certifications,
                  etc.).
                </p>
                {professionalLinks.map((link, index) => (
                  <InputGroup size="sm" className="mb-2" key={`pro-link-${index}`}>
                    <Form.Control
                      type="url"
                      placeholder={
                        index === 0
                          ? 'https://www.linkedin.com/in/your-name'
                          : 'https://github.com/username or portfolio link'
                      }
                      value={link}
                      onChange={(e) => handleProfessionalLinkChange(index, e.target.value)}
                    />
                    <Button
                      variant="outline-danger"
                      className="professional-link-remove-btn"
                      onClick={() => removeProfessionalLinkField(index)}
                      disabled={professionalLinks.length === 1}
                      title="Remove link"
                    >
                      Remove
                    </Button>
                  </InputGroup>
                ))}
                <Button
                  variant="outline-success"
                  onClick={addProfessionalLinkField}
                  disabled={professionalLinks.length >= 10}
                >
                  Add another link ({professionalLinks.length}/10)
                </Button>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowLinksModal(false)}
                  disabled={linksSaveLoading}
                >
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSaveLinks} disabled={linksSaveLoading}>
                  {linksSaveLoading ? <Spinner animation="border" size="sm" /> : 'Save'}
                </Button>
              </Modal.Footer>
            </Modal>
          )}

          <div style={{ margin: '19px auto' }}>
            <Button
              variant="success"
              className="share-profile-button share-button-container"
              onClick={() => {
                if (shareAllowed) {
                  onShareModalOpen();
                } else {
                  onErrorModalOpen();
                }
              }}
            >
              <ShareIcon style={{ width: '15px', height: '15px' }} /> Share Profile
            </Button>
          </div>

          {userData && (
            <div className="user-profile-resume-section" data-profile-focus="resume">
              <p className="user-profile-resume-copy">
                Add or replace your resume so recruiters can review it.
              </p>
              <div className="user-profile-resume-panel">
                <ResumeUpload
                  existingResumeUrl={userData.resume}
                  existingResumeFileName={userData.resumeFileName || userData.resume_filename}
                  uploadImmediately
                  showLabel={false}
                  displayMode="panel"
                  onUploadSuccess={(fileUrl, fileName) => {
                    setUserData?.((prev: any) =>
                      prev
                        ? {
                            ...prev,
                            resume: fileUrl,
                            resumeFileName: fileName || prev.resumeFileName,
                          }
                        : prev
                    );
                    getSessionStore().updateCurrentUser({
                      resume: fileUrl,
                      resumeFileName: fileName,
                    });
                  }}
                />
              </div>
            </div>
          )}

          {showErrorModal && (
            <Modal onHide={() => setShowErrorModal(false)} show>
              <Modal.Header closeButton>
                <Modal.Title>Profile Incomplete</Modal.Title>
              </Modal.Header>
              <Modal.Body>❌ {shareBlockMessage}</Modal.Body>
            </Modal>
          )}
          <ShareProfileModal
            show={show}
            onHide={handleCloseShareModal}
            profileLink={profileLink}
            recruiterEmail={recruiterEmail}
            onRecruiterEmailChange={setRecruiterEmail}
            onCopyLink={copyToClipboard}
            onSendEmail={handleShareWithRecruiter}
            sending={shareLoading}
            sendSuccess={shareEmailSuccess}
            sendError={shareEmailError}
            previewHref={profilePreviewHref}
            durationDays={shareDurationDays}
            onDurationDaysChange={setShareDurationDays}
            candidate={shareCandidate}
          />
        </Card.Body>
      </Card>

      {/* Zoom Image Modal */}
      <Modal show={showImageModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Body className="text-center p-0">
          <Image
            src={userData?.profileImg}
            alt="Profile"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
            fluid
          />
        </Modal.Body>
      </Modal>
      <Modal show={showEditJobRoleModal} onHide={handleCloseModals} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Job Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to modify your job role?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModals}>
            No
          </Button>
          <Button variant="success" onClick={handleEditJobRoleConfirm}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDataWarningModal} onHide={handleCloseModals} centered>
        <Modal.Header closeButton>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>{ROLE_CHANGE_WARNING}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModals}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleDataWarningConfirm}>
            Continue to role selection
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserPanel;
